// ============================================================================
// BOOK · the mount — one full-screen surface over whatever is open (AppOverlays)
// ============================================================================
// Same pattern as ProjectsChooserMount: opened from a store, gated on Orders
// access (fail-closed while permissions load), closed when access is lost.
// Nothing in the Orders bar, the Navigator or OrdersPortal is touched (held
// files, brief §3). Closing returns to the screen underneath.
//   view      Orders access                      (has_orders_access on the server)
//   edit      super admin · Orders editor/admin  (has_orders_editor — the Order Book rule)
//   photos    super admin · any Files role       (has_files_access — the library's RLS)
//   upload    super admin · Files editor/admin   (has_files_editor — useFilesPerms)
// ============================================================================
import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { useCanAccessPortal, usePortalPermissions, usePortalRole } from '../../hooks/usePortalPermissions';
import { isSuperAdmin } from '../../utils/superAdmin';
import { useBookStore } from './bookStore';

const BookScreen = lazy(() => import('./BookScreen'));

export function useBookPerms() {
  const { data } = usePortalPermissions();
  const orders = usePortalRole('orders');
  const files = usePortalRole('files');
  const sa = !!data?.isSuperAdmin || (data?.user && isSuperAdmin(data.user));
  return useMemo(() => ({
    canEdit: sa || orders === 'editor' || orders === 'admin',
    canReadPhotos: sa || ['viewer', 'editor', 'admin'].includes(files),
    canUpload: sa || files === 'editor' || files === 'admin',
    userId: data?.user?.id || null,
  }), [sa, orders, files, data?.user?.id]);
}

export default function BookMount() {
  const allowed = useCanAccessPortal('orders');
  const open = useBookStore((s) => s.open);
  const bookKey = useBookStore((s) => s.bookKey);
  const close = useBookStore((s) => s.closeBook);
  const { isLoading } = usePortalPermissions();
  useEffect(() => { if (open && !allowed && !isLoading) close(); }, [open, allowed, isLoading, close]);
  if (!open || !allowed) return null;
  return (
    <Suspense fallback={null}>
      <BookScreen bookKey={bookKey} onClose={close} />
    </Suspense>
  );
}
